import { Injectable } from '@angular/core';
import { CrmPipelineService, FieldValueInterface, Pipeline, Stage, StandardIds } from '@galaxy/crm/static';
import { BehaviorSubject, distinctUntilChanged, of, shareReplay, switchMap, tap } from 'rxjs';
import { GalaxyFilterInterface, GalaxyFilterOperator } from '@vendasta/galaxy/filter/chips';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StageData, StageMap } from './interfaces';

@Injectable()
export class PipelineSettingsService {
  private readonly currentPipelineId$$ = new BehaviorSubject<string>('');
  private readonly fallbackPipelineId$$ = new BehaviorSubject<string>('');
  readonly fallbackPipelineId$ = this.fallbackPipelineId$$.asObservable();

  private readonly allPipelines$$ = new BehaviorSubject<Pipeline[]>([]);
  allPipelines$ = this.allPipelines$$.asObservable().pipe(distinctUntilChanged());

  private readonly allPipelinesLoading$$ = new BehaviorSubject<boolean>(true);
  allPipelinesLoading$ = this.allPipelinesLoading$$.asObservable();

  private readonly loadingPipeline$$ = new BehaviorSubject<boolean>(true);
  loadingPipeline$ = this.loadingPipeline$$.asObservable();

  readonly currentPipeline$ = this.currentPipelineId$$.pipe(
    tap(() => this.loadingPipeline$$.next(true)),
    switchMap((pipelineId) => (pipelineId ? this.crmPipelineService.getPipeline$(pipelineId) : of(null))),
    tap(() => this.loadingPipeline$$.next(false)),
    shareReplay(1),
  );

  constructor(private readonly crmPipelineService: CrmPipelineService) {
    this.crmPipelineService
      .listPipelines$('Opportunity')
      .pipe(takeUntilDestroyed())
      .subscribe((pipelines) => {
        this.allPipelines$$.next(pipelines);
        this.allPipelinesLoading$$.next(false);
      });

    this.allPipelines$.pipe(takeUntilDestroyed()).subscribe((pipelines) => {
      if (pipelines.length > 0) {
        this.fallbackPipelineId$$.next(pipelines[0].id);
      }
    });
  }

  setPipelineId(pipelineId: string) {
    this.currentPipelineId$$.next(pipelineId);
  }

  updatePipeline$(objectType: string, pipelineId: string, pipelineName: string, stageProbabilities: StageMap) {
    const updatedStages = this.getStagesToUpdate(stageProbabilities);
    const updatedPipelineData = {
      id: pipelineId,
      name: pipelineName,
      stages: updatedStages,
    };

    return this.crmPipelineService.updatePipeline$(objectType, updatedPipelineData).pipe(
      switchMap(() => this.crmPipelineService.getPipeline$(pipelineId)),
      tap((pipeline) => {
        const updatedPipelines = this.allPipelines$$.getValue().map((p) => (p.id === pipelineId ? pipeline : p));
        this.allPipelines$$.next(updatedPipelines);
      }),
    );
  }

  createPipeline$(name: string, stages: StageMap) {
    const pipeline = {
      id: '',
      name: name,
      stages: this.getStagesToUpdate(stages),
    };
    return this.crmPipelineService.createPipeline$('Opportunity', pipeline).pipe(
      tap((pipelineId) =>
        this.allPipelines$$.next([
          ...this.allPipelines$$.getValue(),
          {
            ...pipeline,
            id: pipelineId,
          },
        ]),
      ),
    );
  }

  deletePipeline$(pipelineId: string) {
    return this.crmPipelineService.deletePipeline$(pipelineId).pipe(
      tap(() => {
        if (this.fallbackPipelineId$$.getValue() === pipelineId) {
          this.fallbackPipelineId$$.next(this.allPipelines$$.getValue()?.[0].id);
        }
        const updatedPipelines = this.allPipelines$$.getValue().filter((pipeline) => pipeline.id !== pipelineId);
        this.allPipelines$$.next(updatedPipelines);
        if (updatedPipelines.length === 0) {
          this.currentPipelineId$$.next('');
          this.fallbackPipelineId$$.next('');
        }
      }),
    );
  }

  private getStagesToUpdate(stagesMap: StageMap): Stage[] {
    return Array.from(stagesMap, ([stageName, data]) => ({
      stageName,
      data,
    }))
      .sort((a, b) => {
        // Sort stages by probability. If probability is 0, it should be at the end of the list.
        if (a.data.probability === 0) {
          return 1;
        }
        if (b.data.probability === 0) {
          return -1;
        }
        return a.data.probability - b.data.probability;
      })
      .map(({ stageName, data }, index, sortedStages) => {
        const previousProbability = index > 0 ? sortedStages[index - 1].data.probability : 0;
        return {
          id: data.id || '',
          name: stageName,
          mutations: this.getMutationForStage(data),
          filters: this.getFiltersForStage(data, previousProbability),
        };
      });
  }

  private getMutationForStage(data: StageData): FieldValueInterface[] {
    return [
      {
        fieldId: StandardIds.OpportunityProbability,
        floatValue: data.probability,
      },
      {
        fieldId: StandardIds.OpportunityStatus,
        stringValue: data.status,
      },
    ];
  }

  private getFiltersForStage(data: StageData, previousProbability: number): GalaxyFilterInterface[] {
    const filters: GalaxyFilterInterface[] = [
      {
        fieldId: StandardIds.OpportunityStatus,
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
        values: [{ string: data.status }],
      },
    ];

    if (data.probability === 0) {
      filters.push({
        fieldId: StandardIds.OpportunityProbability,
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_LESS_THAN_OR_EQUAL_TO,
        values: [{ float: 0 }],
        floatTolerance: 0.001,
      });
    } else if (data.probability === 1) {
      filters.push({
        fieldId: StandardIds.OpportunityProbability,
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_GREATER_THAN_OR_EQUAL_TO,
        values: [{ float: 1 }],
        floatTolerance: 0.001,
      });
    } else {
      filters.push(
        {
          fieldId: StandardIds.OpportunityProbability,
          operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_GREATER_THAN,
          values: [{ float: previousProbability }],
          floatTolerance: 0.001,
        },
        {
          fieldId: StandardIds.OpportunityProbability,
          operator: GalaxyFilterOperator.FILTER_OPERATOR_IS_LESS_THAN_OR_EQUAL_TO,
          values: [{ float: data.probability }],
          floatTolerance: 0.001,
        },
      );
    }
    return filters;
  }
}
